import { reducedMotion } from "./motion_pref";

// flair: panels and cards lean toward the pointer with a soft glare, primary keys drift toward the cursor, and
// chi's face follows you and blinks. one delegated pointer listener, one rAF loop that only runs while something
// is moving. off under calm / reduced motion / nojuice, tilt and magnetism also under notilt.

const TILT = ".mod.panel, .card, .tile";
const SKIP = ".rack.arranging, .rack.dragging, .task-board, .sticky-note, .scrap, .scratchpad, .lifted, .drag-origin, .wb, #whiteboard, [draggable='true']";
const MAG = ".pill.accent, button.primary, .key-dock, .key-deck";
const FIELD = "input, textarea, select, [contenteditable]";
const HOLD_MS = 120;

type Lean = { x: number; y: number; tx: number; ty: number; g: number; tg: number; max: number; rect: DOMRect | null };
type Drift = { x: number; y: number; tx: number; ty: number };

const root = document.documentElement;
const leans = new Map<HTMLElement, Lean>();
const drifts = new Map<HTMLElement, Drift>();
let last: PointerEvent | null = null;
let pending = false;
let running = false;
let current: HTMLElement | null = null;
let touchOn = false;
let holdTimer = 0;
let touchX = 0;
let touchY = 0;
let magList: HTMLElement[] = [];
let magAt = 0;
let faceRect: DOMRect | null = null;
let blinkTimer = 0;

const off = (flairOnly: boolean): boolean => reducedMotion() || root.classList.contains("nojuice") || (flairOnly && root.classList.contains("notilt"));
const clamp = (n: number, m: number) => Math.max(-m, Math.min(m, n));

function leanOf(el: HTMLElement): Lean {
  let s = leans.get(el);
  if (!s) leans.set(el, (s = { x: 0, y: 0, tx: 0, ty: 0, g: 0, tg: 0, max: 5, rect: null }));
  return s;
}

function release(el: HTMLElement | null): void {
  const s = el && leans.get(el);
  if (s) { s.tx = 0; s.ty = 0; s.tg = 0; }
}

function releaseAll(): void {
  leans.forEach((_, el) => release(el));
  drifts.forEach((d) => { d.tx = 0; d.ty = 0; });
  current = null;
  touchOn = false;
  window.clearTimeout(holdTimer);
  kick();
}

function pick(target: Element | null): HTMLElement | null {
  if (!target || target.closest(FIELD)) return null;
  const el = (target.closest(".mod.panel") ?? target.closest(TILT)) as HTMLElement | null;
  if (!el || el.closest(SKIP) || el.matches(SKIP)) return null;
  return el;
}

function process(e: PointerEvent): void {
  const touch = e.pointerType === "touch";
  const el = !touch || touchOn ? pick(e.target as Element | null) : null;
  if (el !== current) {
    release(current);
    current = el;
    if (el) {
      const s = leanOf(el);
      s.rect = el.getBoundingClientRect();
      s.max = s.rect.width > 420 ? 3 : s.rect.width > 200 ? 5 : 7;
      if (s.rect.width > 720) { current = null; s.rect = null; }
    }
  }
  const s = current && leanOf(current);
  if (s && current) {
    s.rect ??= current.getBoundingClientRect();
    const r = s.rect;
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    s.tx = clamp(px * 2 - 1, 1);
    s.ty = clamp(py * 2 - 1, 1);
    s.tg = 1;
    current.style.setProperty("--gx", `${(px * 100).toFixed(1)}%`);
    current.style.setProperty("--gy", `${(py * 100).toFixed(1)}%`);
  }
  if (!touch) magnet(e);
  face(e);
}

function magnet(e: PointerEvent): void {
  const now = performance.now();
  if (now - magAt > 1200) { magList = [...document.querySelectorAll<HTMLElement>(MAG)]; magAt = now; }
  for (const el of magList) {
    const d = drifts.get(el);
    const r = el.getBoundingClientRect();
    if (!r.width || (el as HTMLButtonElement).disabled || el.matches(":focus-visible")) { if (d) { d.tx = 0; d.ty = 0; } continue; }
    const cx = r.left + r.width / 2 - (d?.x ?? 0);
    const cy = r.top + r.height / 2 - (d?.y ?? 0);
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const reach = 90 + Math.max(r.width, r.height) / 2;
    const dist = Math.hypot(dx, dy);
    const pull = dist < reach ? 1 - dist / reach : 0;
    if (!pull && !d) continue;
    const m = d ?? { x: 0, y: 0, tx: 0, ty: 0 };
    drifts.set(el, m);
    m.tx = clamp(dx * pull * 0.3, 6);
    m.ty = clamp(dy * pull * 0.3, 6);
  }
}

// chi's face slides a pixel or two toward the pointer
function face(e: PointerEvent): void {
  const av = document.getElementById("today-face");
  if (!av) return;
  faceRect ??= av.getBoundingClientRect();
  if (!faceRect.width) { faceRect = null; return; }
  const k = (v: number, c: number) => clamp((v - c) / 320, 1) * 2.2;
  av.style.setProperty("--ax", `${k(e.clientX, faceRect.left + faceRect.width / 2).toFixed(2)}px`);
  av.style.setProperty("--ay", `${k(e.clientY, faceRect.top + faceRect.height / 2).toFixed(2)}px`);
  ensureBlink();
}

function frame(): void {
  running = false;
  if (pending && last) {
    pending = false;
    if (off(false)) releaseAll();
    else if (!off(true)) process(last);
    else face(last);
  }
  let busy = pending;
  leans.forEach((s, el) => {
    s.x += (s.tx - s.x) * 0.2;
    s.y += (s.ty - s.y) * 0.2;
    s.g += (s.tg - s.g) * 0.16;
    const rest = !s.tg && Math.abs(s.x) < 0.003 && Math.abs(s.y) < 0.003 && s.g < 0.01;
    if (rest) {
      el.style.removeProperty("transform");
      el.style.removeProperty("--ga");
      el.style.removeProperty("--gx");
      el.style.removeProperty("--gy");
      el.removeAttribute("data-tilt");
      leans.delete(el);
      return;
    }
    busy = true;
    el.dataset.tilt = "1";
    el.style.transform = `perspective(800px) rotateX(${(-s.y * s.max).toFixed(2)}deg) rotateY(${(s.x * s.max).toFixed(2)}deg)`;
    el.style.setProperty("--ga", s.g.toFixed(2));
  });
  drifts.forEach((d, el) => {
    d.x += (d.tx - d.x) * 0.22;
    d.y += (d.ty - d.y) * 0.22;
    if (!d.tx && !d.ty && Math.abs(d.x) < 0.05 && Math.abs(d.y) < 0.05) {
      el.style.removeProperty("translate");
      drifts.delete(el);
      return;
    }
    busy = true;
    el.style.translate = `${d.x.toFixed(2)}px ${d.y.toFixed(2)}px`;
  });
  if (busy) kick();
}

function kick(): void {
  if (running) return;
  running = true;
  requestAnimationFrame(frame);
}

function blink(): void {
  blinkTimer = 0;
  const av = document.getElementById("today-face");
  if (document.hidden || !av || !av.offsetParent) return; // chain stops, restarts on the next pointer move
  if (!off(false)) {
    av.classList.remove("blink");
    void av.offsetWidth;
    av.classList.add("blink");
  }
  blinkTimer = window.setTimeout(blink, 3000 + Math.random() * 4000);
}

function ensureBlink(): void {
  if (!blinkTimer && !off(false)) blinkTimer = window.setTimeout(blink, 1500 + Math.random() * 2500);
}

export function setupTilt(): void {
  document.addEventListener("pointermove", (e) => {
    last = e;
    if (e.pointerType === "touch") {
      if (!touchOn && Math.hypot(e.clientX - touchX, e.clientY - touchY) > 8) window.clearTimeout(holdTimer);
      if (!touchOn) return;
    }
    pending = true;
    kick();
  }, { passive: true });

  document.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "touch") return;
    touchX = e.clientX;
    touchY = e.clientY;
    window.clearTimeout(holdTimer);
    holdTimer = window.setTimeout(() => {
      if (off(true)) return;
      touchOn = true;
      last = e;
      pending = true;
      kick();
    }, HOLD_MS);
  }, { passive: true });

  for (const type of ["pointerup", "pointercancel"]) document.addEventListener(type, (e) => { if ((e as PointerEvent).pointerType === "touch") releaseAll(); }, { passive: true });
  root.addEventListener("pointerleave", releaseAll);
  window.addEventListener("blur", releaseAll);
  const stale = () => { faceRect = null; for (const s of leans.values()) s.rect = null; magAt = 0; };
  window.addEventListener("scroll", stale, { capture: true, passive: true });
  window.addEventListener("resize", stale, { passive: true });
  document.addEventListener("visibilitychange", () => { if (document.hidden) { window.clearTimeout(blinkTimer); blinkTimer = 0; } });
  ensureBlink();
  document.addEventListener("animationend", (e) => { if ((e.target as Element).classList?.contains("blink")) (e.target as Element).classList.remove("blink"); });
}
